import React, { useContext, useEffect, useRef, useState } from 'react';
import { useFetchRequiredCredits, useGenerateAiMedia } from '../../../t2c-fe/services';
import { useSnackbar } from 'notistack';
import { AIMedia, AudioCredit, Avatar, UploadTypes } from '../../../t2c-fe/@types';
import { useDialog } from '../useDialog';
import { useRouter } from 'next/router';
import { useAppContext } from '../context';
import { useCreditContext } from '../useCreditContext';
import { AspectRatios } from '@t2c/ai-video-editor';

import {
  isCreditsAvailable,
  NumGenerationsEnum,
  AiVideoModelEnum,
  AvatarDataType,
} from '../../../t2c-fe/utils';
import { getRandomInt, VIDEO_DEFAULT_NAME_PREFIX } from '../../utils';
import { useEnhancePrompt } from './useEnchancePrompt';
import { FeatureType, useGetUserEligibility } from '../useGetUserEligibility';
import { usePathname } from 'next/navigation';
import { resizeImageFile } from 'apps/t2c-fe/components/common/ResizeFunction';
import { resolveMediaUploadId } from 'apps/t2c-fe/utils/resolveMediaUploadId';

export const useBrollPage = ({ refetchBrollClips }) => {
  const [clipGenerated, setClipGenerated] = useState(false);
  const [openRenderGateModal, setOpenRenderGateModal] = useState(false);
  const [renderGateModalMode, setRenderGateModalMode] = useState<
    'processing' | 'upgrade' | 'noPlan'
  >('upgrade');
  const pathName = usePathname();

  const { setUsableCredits, usableCredits, modelDetails } = useCreditContext();
  const abortController = useRef(null);
  const abortTailController = useRef(null);

  const [uploadURL, setUploadURL] = useState('');
  const [uploadTailURL, setUploadTailURL] = useState('');
  const [isSelectedTail, setIsSelectedTail] = useState(false);
  const [isImage, setIsImage] = useState(true);
  const [maxCharLimit, setMaxCharLimit] = useState<number>(
    modelDetails[AiVideoModelEnum.SEEDANCE2_5].maxCharLimit
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isTailuploading, setIsTailUploading] = useState(false);
  const [selectedCharacterImage, setSelectedCharacterImage] = useState<string | null>(null);
  const [isCharacterImage, setIsCharacterImage] = useState(false);
  const [clipGenerationData, setClipGenerationData] = useState({
    prompt:
      'Ultra-realistic cinematic holiday scene. Santa Claus drops onto a couch in a modern living room, exhausted, beard slightly crooked. Santa stares dead-pan into the camera and says "You... naughty list!" Christmas tree lights flicker. Tone: dry humor, modern chaos. Camera: smooth cinematic push-in, warm holiday lighting.',
    name: VIDEO_DEFAULT_NAME_PREFIX + getRandomInt(1, 1000),
    uploadId: '',
    uploadTailId: '',
    duration: modelDetails[AiVideoModelEnum.SEEDANCE2_5].durationPerModel[0],
    numGenerations: parseInt(NumGenerationsEnum.ONE),
    orientation: modelDetails[AiVideoModelEnum.SEEDANCE2_5].orientationPerModel[0],
    resolution: modelDetails[AiVideoModelEnum.SEEDANCE2_5].resolutionPerModel[0],
    model: AiVideoModelEnum.SEEDANCE2_5,
    generateAudio: true,
    enhancePrompt: false,
    bodyControl: false,
    multiShot: modelDetails[AiVideoModelEnum.KLING_V3_PRO].mutliShot[0],
  });
  const [newGenerationData, setNewGenerationData] = useState<AIMedia>();
  const [creditsData, setCreditsData] = useState<AudioCredit | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [imageFile, setImageFile] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showSnack, setShowSnack] = useState(false);
  const supportedFormats = [
    // 'mp4',
    'jpg',
    'jpeg',
    'png',
  ];

  const handleResolutionvalue = (value: string) => {
    return value === '1080p'
      ? 1080
      : value === '720p'
      ? 720
      : value === '480p'
      ? 480
      : value === '360p'
      ? 360
      : 240;
  };

  const { enqueueSnackbar } = useSnackbar();

  const {
    showAiGenModel: isProccessingModalOpen,
    handleOpenAiGenModal: openProccessingModal,
    handleCloseAiGenModal: closeProccessingModal,
    showCreditDialog: showCreditDialog,
  } = useAppContext();

  const {
    mutateAsync: mutateFetchRequiredCredits,
    isLoading: isFetchingRequiredCredits,
    data: requiredCreditsData,
    isError: isCreditFetchError,
  } = useFetchRequiredCredits();

  const { mutateAsync: mutateEnhancePrompt, isLoading: isEnhancing } = useEnhancePrompt();

  const {
    closeDialog: closeCreditDialog,
    isDialogOpen: isCreditDialogOpen,
    openDialog: openCreditDialog,
  } = useDialog();

  const {
    isDialogOpen: isSelectCharacterDialog,
    openDialog: openSelectCharacterDialog,
    closeDialog: closeSelectCharacterDialog,
  } = useDialog();

  const { mutateAsync: getUserEligibility, isLoading: isCheckingEligibility } =
    useGetUserEligibility();
  const { mutateAsync: generateAiBrollClip, isLoading: clipGenerationLoading } =
    useGenerateAiMedia();

  const handleCharacterSelect = (data: AvatarDataType | Avatar) => {
    setClipGenerationData((prev) => ({ ...prev, uploadTailId: data?.uploadId }));
    setIsCharacterImage(!data?.isPublic && data?.uploads?.type === UploadTypes.Image);
    setUploadTailURL(data?.uploads?.url || '');
    setSelectedCharacterImage(data?.thumbnail || null);
    closeSelectCharacterDialog();
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchWithRetry = async (
    url: string,
    options?: RequestInit,
    config: { attempts?: number; baseDelayMs?: number; maxDelayMs?: number } = {}
  ): Promise<Response> => {
    const attempts = config.attempts ?? 5;
    const baseDelayMs = config.baseDelayMs ?? 350;
    const maxDelayMs = config.maxDelayMs ?? 2500;

    let lastError: unknown;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          cache: 'no-store',
          credentials: 'omit',
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} ${response.statusText}`);
        }
        return response;
      } catch (error) {
        lastError = error;
        if (attempt === attempts) break;

        const expBackoff = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1));
        const jitter = Math.floor(Math.random() * 150);
        await sleep(expBackoff + jitter);
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to fetch media');
  };

  const handleMediaSelect = (url: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      const isTailUploadSelection = isImage && isSelectedTail;
      const setActiveUploading = isTailUploadSelection ? setIsTailUploading : setIsUploading;
      setActiveUploading(true);

      let objectId = '';
      try {
        const pathname = new URL(url).pathname;
        const parts = pathname.split('/');
        objectId = parts[parts.length - 1];
      } catch {
        objectId = '';
      }

      try {
        const response = await fetchWithRetry(url);
        let blob = await response.blob();
        const sizeMB = blob.size / (1024 * 1024);
        const maxSize = modelDetails[clipGenerationData.model].maxImageSize ?? 10;
        if (sizeMB >= maxSize) {
          if (isImage) {
            const filePreview = URL.createObjectURL(blob);
            blob = await resizeImageFile(filePreview, {
              maxWidth: 2048,
              maxHeight: 2048,
              targetMaxBytes: maxSize * 1024 * 1024,
            });
          } else {
            enqueueSnackbar(`Video must be smaller than ${maxSize}MB.`, {
              variant: 'error',
            });
            setActiveUploading(false);
            return reject(new Error(`Video must be smaller than ${maxSize}MB.`));
          }
        }

        const objectUrl = URL.createObjectURL(blob);
        if (!isImage) {
          const file = new File([blob], 'video.mp4', { type: blob.type });
          try {
            await preValidator(file);
          } catch (validationError) {
            enqueueSnackbar(validationError.message, { variant: 'error' });
            setActiveUploading(false);
            return reject(validationError);
          }
          const { duration, width, height } = await getVideoMetadata(objectUrl);

          if (clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO) {
            if (duration < 3 || duration > 30) {
              enqueueSnackbar(`Video duration must be between 3 to 30 seconds.`, {
                variant: 'error',
              });
              return reject(new Error(`Video duration must be between 3 to 30 seconds.`));
            }
          }
          if (clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO) {
            if (duration < 3 || duration > 30) {
              enqueueSnackbar(`Video duration must be between 3 to 30 seconds.`, {
                variant: 'error',
              });
              return reject(new Error(`Video duration must be between 3 to 30 seconds.`));
            }
          }

          const resolvedUploadId = await resolveMediaUploadId({
            sourceUrl: url,
            blob,
            format: 'video',
            candidateId: objectId,
            signal: abortController.current?.signal,
          });
          if (!resolvedUploadId) {
            setActiveUploading(false);
            return reject(new Error('Unable to resolve upload id for selected video.'));
          }

          setClipGenerationData((prev) => ({
            ...prev,
            uploadId: resolvedUploadId,
            duration: Math.round(duration),
          }));
          setUploadURL(url);
          setActiveUploading(false);

          resolve();
        } else {
          const file = new File([blob], 'image.jpg', { type: blob.type });
          try {
            await preValidator(file);
          } catch (validationError) {
            const filePreview = URL.createObjectURL(file);
            setShowEditor(true);
            setImageFile(filePreview);
            setActiveUploading(false);
            return reject(validationError);
          }

          const img = new Image();
          img.crossOrigin = 'anonymous';

          img.onload = () => {
            (async () => {
              try {
                URL.revokeObjectURL(objectUrl);
                const resolvedUploadId = await resolveMediaUploadId({
                  sourceUrl: url,
                  blob,
                  format: 'image',
                  candidateId: objectId,
                  signal: isSelectedTail
                    ? abortTailController.current?.signal
                    : abortController.current?.signal,
                });
                if (!resolvedUploadId) {
                  setActiveUploading(false);
                  return reject(new Error('Unable to resolve upload id for selected image.'));
                }

                if (isSelectedTail) {
                  setClipGenerationData((prev) => ({ ...prev, uploadTailId: resolvedUploadId }));
                  setUploadTailURL(url);
                } else {
                  setClipGenerationData((prev) => ({ ...prev, uploadId: resolvedUploadId }));
                  setUploadURL(url);
                }

                setActiveUploading(false);
                resolve();
              } catch (error) {
                setActiveUploading(false);
                reject(error);
              }
            })();
          };

          img.onerror = () => {
            setActiveUploading(false);
            reject(new Error('Failed to load image.'));
          };
          img.src = objectUrl;
          setShowEditor(false);
          setImageFile('');
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        if (
          err instanceof TypeError ||
          /failed to fetch|networkerror|cors|blocked by cors/i.test(errMsg)
        ) {
          enqueueSnackbar(
            'Unable to load this media due to network security restrictions. Please try again in a moment.',
            { variant: 'error' }
          );
        }
        setActiveUploading(false);
        reject(err);
      }
    });
  };

  const getVideoMetadata = (
    fileOrUrl: File | string
  ): Promise<{ duration: number; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const duration = video.duration;
        const width = video.videoWidth;
        const height = video.videoHeight;
        resolve({ duration, width, height });
      };

      video.onerror = () => {
        reject(new Error('Failed to load video metadata'));
      };

      if (typeof fileOrUrl === 'string') {
        video.src = fileOrUrl;
      } else {
        video.src = URL.createObjectURL(fileOrUrl);
      }
    });
  };

  const handleUpdateStartImageId = (uploadId: string, url: string, duration?: number) => {
    setClipGenerationData((prev) => ({
      ...prev,
      uploadId: uploadId,
      duration: duration ?? prev.duration,
    }));
    setUploadURL(url);
  };

  const handleUpdateEndImageId = (uploadId: string, url: string) => {
    setClipGenerationData((prev) => ({ ...prev, uploadTailId: uploadId }));
    setUploadTailURL(url);
  };

  const handleDataChange = (key: string, value: string) => {
    setClipGenerationData((prev) => ({
      ...prev,
      [key]: value && (key === 'duration' || key === 'numGenerations') ? parseInt(value) : value,
    }));
  };

  const handleAudioToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClipGenerationData((prev) => ({ ...prev, generateAudio: event.target.checked }));
  };

  const handleEnhancePromptToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClipGenerationData((prev) => ({ ...prev, enhancePrompt: event.target.checked }));
  };

  const handleBodyControlToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClipGenerationData((prev) => ({ ...prev, bodyControl: event.target.checked }));
  };

  const handleClipGenerate = async () => {
    let errMsg = '';
    if (!clipGenerationData.name.trim()) errMsg = 'Please Enter The Name.';
    if (
      !clipGenerationData.prompt.trim() &&
      clipGenerationData.model !== AiVideoModelEnum.RUNWAY_ACT_TWO &&
      clipGenerationData.model !== AiVideoModelEnum.KLING_V3_MOTION_CONTROL
    )
      errMsg = 'Please Enter A Prompt.';
    if (clipGenerationData.uploadTailId && !clipGenerationData.uploadId)
      errMsg = 'Please Select A Start Image.';
    if (clipGenerationData.duration === 0) errMsg = 'Duration is too less';
    if (clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO) {
      if (!clipGenerationData.uploadTailId) {
        errMsg = 'Please Select A Character.';
      }
      if (clipGenerationData.duration < 3 || clipGenerationData.duration > 30) {
        errMsg = 'Duration of video should be between 3 to 30 seconds';
      }
    }
    if (
      clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO &&
      !clipGenerationData.uploadId
    ) {
      errMsg = 'Please Select A Reference Video.';
    }
    if (
      modelDetails[clipGenerationData.model].isStartImageRequired &&
      !clipGenerationData.uploadId
    ) {
      errMsg = 'Please Select A Start Image.';
    }
    if (
      modelDetails[clipGenerationData.model].isEndImageRequired &&
      !clipGenerationData.uploadTailId
    ) {
      errMsg = 'Please Select A Start Image.';
    }
    if (clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL) {
      if (!clipGenerationData.uploadId) {
        errMsg = 'Please Select A Reference Video.';
      } else if (!clipGenerationData.uploadTailId) {
        errMsg = 'Please Select A Reference Image.';
      } else if (
        clipGenerationData.bodyControl &&
        (clipGenerationData.duration < 3 || clipGenerationData.duration > 30)
      ) {
        errMsg = 'Duration of video should be between 3 to 30 seconds';
      } else if (
        !clipGenerationData.bodyControl &&
        (clipGenerationData.duration < 3 || clipGenerationData.duration > 10)
      ) {
        errMsg = 'Duration of video for image orientation should be less than 10 seconds';
      }
    }

    if (
      (clipGenerationData.model === AiVideoModelEnum.VEO3_1 ||
        clipGenerationData.model === AiVideoModelEnum.VEO3_1_FAST) &&
      (clipGenerationData.uploadId ||
        clipGenerationData.uploadTailId ||
        clipGenerationData.resolution === '1080p') &&
      clipGenerationData.duration < 8
    ) {
      errMsg =
        'For Veo 3.1, duration must be at least 8 seconds' +
        (clipGenerationData.resolution === '1080p'
          ? ' for 1080p resolution.'
          : ' when an image is selected.');
    }
    if (errMsg) {
      enqueueSnackbar(errMsg, { variant: 'error' });
      return;
    }

    const generateMediaData = {
      clipGenerationData,
      isCharacterImage,
      uploadTailURL,
      uploadURL,
      selectedCharacterImage,
      url: pathName,
    };
    localStorage.setItem('generateMedia', JSON.stringify(generateMediaData));

    const eligibility = await getUserEligibility({
      featureType: FeatureType.BROLL,
      model: clipGenerationData.model,
    });
    if (!eligibility.eligible) {
      if (eligibility.isPremium) {
        if (eligibility.noPlan) {
          setOpenRenderGateModal(true);
          setRenderGateModalMode('noPlan');
          return;
        }
        setOpenRenderGateModal(true);
        setRenderGateModalMode('upgrade');
        return;
      }
      enqueueSnackbar('Daily generation limit reached for this model. Try again later.', {
        variant: 'error',
      });
      return;
    }
    if (eligibility.remainingBrolls < clipGenerationData.numGenerations) {
      enqueueSnackbar(
        'Daily generation limit reached for this model. Try decreasing generations count.',
        { variant: 'error' }
      );
      return;
    }
    const res = await mutateFetchRequiredCredits({
      text: clipGenerationData.prompt,
      aiGenType: 'video',
      duration: clipGenerationData.duration,
      numGenerations: clipGenerationData.numGenerations,
      model: clipGenerationData.model,
    });
    setCreditsData(res);
  };

  const handlePromptEnhance = async () => {
    try {
      const res = await mutateEnhancePrompt({
        prompt: clipGenerationData.prompt,
        model: 'b-roll',
      });
      handleDataChange('prompt', res);
    } catch (error) {
      enqueueSnackbar('Failed to enchance prompt', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (localStorage.getItem('generateMedia')) {
      let data;
      try {
        data = JSON.parse(localStorage.getItem('generateMedia'));
      } catch {
        localStorage.removeItem('generateMedia');
        return;
      }

      if (data?.complete === true) {
        setTimeout(() => {
          const saved = data.clipGenerationData ?? {};
          if (!modelDetails[saved.model as AiVideoModelEnum]) {
            setClipGenerationData((prev) => ({
              ...prev,
              prompt: saved.prompt || prev.prompt,
              name: saved.name || prev.name,
            }));
            localStorage.removeItem('generateMedia');
            enqueueSnackbar(
              'The model you selected is no longer available. Please review your settings.',
              { variant: 'info' }
            );
            return;
          }

          setClipGenerationData({ ...saved });
          if (data.uploadURL) {
            setUploadURL(data.uploadURL);
          }
          if (data.uploadTailURL) {
            setUploadTailURL(data.uploadTailURL);
          }
          if (data.selectedCharacterImage) {
            setSelectedCharacterImage(selectedCharacterImage);
          }
          setIsCharacterImage(!!data.isCharacterImage);
        }, 0);
      }
    }
  }, []);

  useEffect(() => {
    if (creditsData) {
      const isEnoughCredit = isCreditsAvailable(
        creditsData?.availableCredits,
        creditsData?.creditsInUse,
        creditsData?.requiredCredits
      );
      if (showCreditDialog) {
        openCreditDialog();
      } else {
        if (!isFetchingRequiredCredits && isEnoughCredit) {
          handleConfirmGeneration();
        } else if (!isEnoughCredit) {
          // handleUpgrade();
          openCreditDialog();
        }
      }
    }
  }, [creditsData]);

  const handleConfirmGeneration = async () => {
    closeCreditDialog();
    setNewGenerationData(null);

    try {
      const requests = Array.from({ length: clipGenerationData.numGenerations }, () =>
        generateAiBrollClip({
          category: 'video',
          name: clipGenerationData.name.trim(),
          uploadId: clipGenerationData.uploadId,
          uploadTailId: clipGenerationData.uploadTailId,
          prompt: clipGenerationData.prompt.trim(),
          duration: clipGenerationData.duration,
          orientation: clipGenerationData.orientation,
          resolution: handleResolutionvalue(clipGenerationData.resolution),
          model: clipGenerationData.model,
          generateAudio: clipGenerationData.generateAudio,
          enhancePrompt: clipGenerationData.enhancePrompt,
          bodyControl: clipGenerationData.bodyControl,
          isCharacterImage: isCharacterImage,
          multiShot: clipGenerationData.multiShot,
        })
      );
      const res = await Promise.all(requests);
      localStorage.removeItem('generateMedia');
      setNewGenerationData(res[0]);
      setUsableCredits(usableCredits - creditsData.requiredCredits);
      openProccessingModal();
      await refetchBrollClips();
    } catch (error) {
      enqueueSnackbar('Failed to generate', { variant: 'error' });
    }
  };

  const { push } = useRouter();
  const handleUpgrade = () => {
    push('/subscription');
  };

  const preValidator = async (file: File) => {
    let dimensions;
    const model = clipGenerationData.model;

    if (file?.type?.includes('video') || file?.name?.endsWith('.mp4')) {
      dimensions = await getVideoMetadata(file);
      if (model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL) {
        const isVideoOrientation = clipGenerationData.bodyControl;

        if (file.size > 99 * 1024 * 1024) {
          throw new Error('Video must be smaller than 100MB.');
        }

        if (
          Math.min(dimensions.width, dimensions.height) < 340 ||
          Math.max(dimensions.width, dimensions.height) > 3850
        ) {
          const message = `Invalid video resolution. The short edge must be atleast 340px and the long edge must not exceed 3850px.`;
          throw new Error(message);
        }

        if (dimensions.duration < 3 || (isVideoOrientation && dimensions.duration > 30)) {
          throw new Error('Video duration for Video Orientation must be between 3 to 30 seconds.');
        }
        if (dimensions.duration < 3 || (!isVideoOrientation && dimensions.duration > 10)) {
          throw new Error('Video duration for Image Orientation must be between 3 to 10 seconds.');
        }
      }
      return;
    } else {
      dimensions = await getImageDimensions(file);
    }

    // General image validations
    if (dimensions.width < 300 || dimensions.height < 300) {
      setErrorMsg('Image must be at least 300x300 pixels.');
      throw new Error('Image must be at least 300x300 pixels.');
    }
    const aspectRatio = dimensions.width / dimensions.height;
    if (aspectRatio < 0.4 || aspectRatio > 2.5) {
      setErrorMsg('Aspect ratio must be between 1:2.5 and 2.5:1.');
      throw new Error('Aspect ratio must be between 1:2.5 and 2.5:1.');
    }

    if (
      (clipGenerationData.model === AiVideoModelEnum.VEO3_1 ||
        clipGenerationData.model === AiVideoModelEnum.VEO3_1_FAST) &&
      clipGenerationData.duration < 8
    ) {
      setClipGenerationData((prev) => ({ ...prev, duration: 8 }));
      enqueueSnackbar(
        'Veo 3.1 requires at least 8 seconds when an image is selected.  Duration auto-set to 8 seconds.',
        {
          variant: 'info',
        }
      );
    }

    if (model === AiVideoModelEnum.KLING_V2_6_PRO) {
      let orientation: AspectRatios;
      if (dimensions.width > dimensions.height) {
        orientation = AspectRatios['16_9'];
      } else if (dimensions.height === dimensions.width) {
        orientation = AspectRatios['1_1'];
      } else {
        orientation = AspectRatios['9_16'];
      }

      setClipGenerationData((prev) => ({ ...prev, orientation }));
      enqueueSnackbar(`Orientation auto-set to ${orientation} based on image dimensions.`, {
        variant: 'info',
      });
    }

    if (model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL) {
      const maxBytes = 10 * 1024 * 1024;
      if (file.size > maxBytes) {
        const filePreview = URL.createObjectURL(file);
        file = await resizeImageFile(filePreview, { maxWidth: 1280, maxHeight: 720 });
      }

      if (
        dimensions.width < 300 ||
        dimensions.height < 300 ||
        dimensions.width > 65536 ||
        dimensions.height > 65536
      ) {
        throw new Error(
          'Invalid image resolution. Width and height must be between 300px and 65536px.'
        );
      }

      const aspectRatio = dimensions.width / dimensions.height;
      if (aspectRatio < 0.4 || aspectRatio > 2.5) {
        throw new Error('Aspect ratio must be between 1:2.5 and 2.5:1.');
      }
    }
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  useEffect(() => {
    const shouldValidateModel =
      modelDetails[clipGenerationData.model].showUpload ||
      modelDetails[clipGenerationData.model].showTailUpload;

    if (!shouldValidateModel || (!uploadURL && !uploadTailURL)) return;

    const validateExistingImage = async (
      selectedUrl: string,
      isTail: boolean,
      clearImage: () => void
    ) => {
      if (!selectedUrl) return;
      try {
        const response = await fetchWithRetry(selectedUrl);
        const blob = await response.blob();
        const file = new File([blob], 'selected-image.jpg', {
          type: blob.type || 'image/jpeg',
        });
        await preValidator(file);
      } catch (error) {
        try {
          const response = await fetchWithRetry(selectedUrl);
          const blob = await response.blob();
          const filePreview = URL.createObjectURL(blob);
          clearImage();
          setIsImage(true);
          setIsSelectedTail(isTail);
          setErrorMsg(
            error instanceof Error
              ? error.message
              : 'Selected image does not match updated model settings.'
          );
          setImageFile(filePreview);
          setShowEditor(true);
        } catch {
          clearImage();
          enqueueSnackbar('Unable to revalidate selected image after model changes.', {
            variant: 'error',
          });
        }
      }
    };

    validateExistingImage(uploadURL, false, () => handleUpdateStartImageId('', ''));
    validateExistingImage(uploadTailURL, true, () => handleUpdateEndImageId('', ''));
  }, [
    clipGenerationData.model,
    clipGenerationData.orientation,
    clipGenerationData.resolution,
    clipGenerationData.bodyControl,
  ]);

  const applyModelDefaults = (model: AiVideoModelEnum) => {
    const config = modelDetails[model];

    setClipGenerationData((prev) => ({
      ...prev,
      model,

      prompt: prev.prompt
        ? prev.prompt
        : model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL
        ? 'Smooth cinematic camera motion. Slow dolly forward with stable framing and perfect subject focus. Subtle background parallax, natural motion blur, realistic depth of field. Fluid, realistic movement with no jitter, warping, or shake. Professional cinematic look.'
        : 'Ultra-realistic cinematic holiday scene. Santa Claus drops onto a couch in a modern living room, exhausted, beard slightly crooked. Santa stares dead-pan into the camera and says "You... naughty list!" Christmas tree lights flicker. Tone: dry humor, modern chaos. Camera: smooth cinematic push-in, warm holiday lighting.',

      duration: config.durationPerModel[0] ?? 5,

      orientation: config.orientationPerModel[0] ?? AspectRatios['16_9'],

      resolution: config.resolutionPerModel[0] ?? '720p',

      uploadId: '',
      uploadTailId: '',
      generateAudio: config.showAudio ?? model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL,

      enhancePrompt: config.showEnhancePrompt ?? false,
      bodyControl: config.showBodyControl ?? false,
    }));

    setUploadURL('');
    setUploadTailURL('');
    setSelectedCharacterImage(null);
    // Reset localStorage data
    localStorage.removeItem('generateMedia');

    const maxchar = modelDetails[clipGenerationData.model].maxCharLimit;
    setMaxCharLimit(maxchar);
    if (clipGenerationData.prompt.length > maxchar) {
      handleDataChange('prompt', clipGenerationData.prompt.slice(0, maxchar));
    }
    // cancel the uploads if any
    if (abortController.current) {
      abortController.current.abort();
      setIsUploading(false);
    }
    if (abortTailController.current) {
      abortTailController.current.abort();
      setIsTailUploading(false);
    }
  };

  useEffect(() => {
    if (showSnack) {
      enqueueSnackbar(
        'Access to Seedance 2.0 is restricted in certain jurisdictions (including the US and Japan). Your user-name is associated with each video generation for compliance and safety monitoring. By proceeding, you acknowledge these terms of service.',
        { variant: 'info' }
      );
    }
  }, [showSnack]);

  const handleMenuClick = (model: AiVideoModelEnum) => {
    // if (model === AiVideoModelEnum.SEEDANCE2_0 || model === AiVideoModelEnum.SEEDANCE2_0_FAST) {
    //   setShowSnack(true);
    //   setTimeout(() => {
    //     setShowSnack(false);
    //   }, 2000);
    // } else {
    //   setShowSnack(false);
    // }
  };

  return {
    uploadURL,
    isImage,
    setIsImage,
    clipGenerated,
    isTailuploading,
    setIsTailUploading,
    isUploading,
    setIsUploading,
    applyModelDefaults,
    setClipGenerated,
    supportedFormats,
    clipGenerationData,
    maxCharLimit,
    setMaxCharLimit,
    setClipGenerationData,
    handleDataChange,
    handleAudioToggle,
    handleEnhancePromptToggle,
    handleClipGenerate,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    isCheckingEligibility,
    requiredCreditsData,
    handleConfirmGeneration,
    clipGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    newGenerationData,
    abortController,
    abortTailController,
    setUploadURL,
    setUploadTailURL,
    handleUpdateStartImageId,
    handleUpdateEndImageId,
    handleUpgrade,
    handleMediaSelect,
    uploadTailURL,
    setIsSelectedTail,
    selectedCharacterImage,
    setSelectedCharacterImage,
    handleCharacterSelect,
    isSelectCharacterDialog,
    openSelectCharacterDialog,
    closeSelectCharacterDialog,
    handleBodyControlToggle,
    handlePromptEnhance,
    openRenderGateModal,
    renderGateModalMode,
    setOpenRenderGateModal,
    setRenderGateModalMode,
    isEnhancing,
    preValidator,
    setImageFile,
    setShowEditor,
    showEditor,
    imageFile,
    errorMsg,
    setErrorMsg,
    handleMenuClick,
  };
};
